import { computed } from 'mobx'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import type { ActivationLifecycle } from '@/infra/activation'
import type { SubmittedJob } from '@/features/jobs/details/SubmittedJob'
import type ConsultationManagerViewModel from '@/features/jobs/details/consultation/ConsultationManagerViewModel'
import type ConsultationSchedulerViewModel from '@/features/jobs/details/consultation/ConsultationSchedulerViewModel'
import { task } from 'mobx-task'
import { firstValueFrom, Subject } from 'rxjs'
import { delay } from 'fejl'
import { fromAbortSignal } from '@/utils/rx-util'
import type { SubmittedJobStore } from '@/features/jobs/details/SubmittedJobStore'

export class ConsultationInfoGatheringViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  /** @see {infoGatheringCompleted$} */
  private readonly _infoGatheringCompletedSubject = new Subject<unknown>()

  /**
   * Emits when info gathering is completed.
   */
  readonly infoGatheringCompleted$ =
    this._infoGatheringCompletedSubject.asObservable()

  constructor(
    private readonly _job: SubmittedJob,
    private readonly _consultationManagerVm: ConsultationManagerViewModel,
    private readonly _submittedJobStore: SubmittedJobStore,
  ) {}

  /**
   * The consultation scheduler view model that is used for this job.
   */
  @computed
  get consultationSchedulerVm(): ConsultationSchedulerViewModel {
    return this._consultationManagerVm.consultationSchedulerVm
  }

  /**
   * Whether we're able to complete info gathering.
   */
  @computed
  get canComplete(): boolean {
    return this.consultationSchedulerVm.hasMinimumSelectedTimes
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }

  /**
   * Completes info gathering by submitting availability.
   */
  completeInfoGathering = task.resolved(async () => {
    if (!this.canComplete) {
      return
    }

    // Submitting availability will also transition out of info gathering
    // for consultation jobs.
    await this.consultationSchedulerVm.submitAvailability()

    // We'll need to bust the cache for and re-fetch the job to make sure
    // the following redirect to job details isn't reading a stale status.
    let attempts = 0
    while (true) {
      const job = await this._submittedJobStore.fetchJob(this._job.id, {
        force: true,
      })
      if (job?.status !== 'InfoGathering') {
        this._infoGatheringCompletedSubject.next(undefined)
        break
      }
      attempts++
      if (attempts > 30) {
        throw new Error(
          'Timed out waiting for job to transition out of info gathering',
        )
      }
      await delay(500)
    }

    // At this point, we know we succeeded, and a navigation to the
    // job details screen is currently pending.
    // To avoid flashing the info gathering UI back to being interactive
    // again, we'll just wait indefinitely here.
    await firstValueFrom(fromAbortSignal(this._lifecycle.signal), {
      defaultValue: null,
    })
  })
}
